import React, { useState, useEffect } from 'react';
import { Mail, Send, X, Plus, Trash2, Paperclip, Loader2 } from 'lucide-react';
import { sendReportEmail, getDefaultRecipients } from '../utils/api';
import { toast } from 'sonner';

const SendReportModal = ({ isOpen, onClose, reportType, weekStartDate }) => {
  const [toEmails, setToEmails] = useState([]);
  const [customEmail, setCustomEmail] = useState('');
  const [attachPdf, setAttachPdf] = useState(false);
  const [attachCsv, setAttachCsv] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [defaultRecipients, setDefaultRecipients] = useState([]);

  const token = localStorage.getItem('agentToken');
  const agentInfo = JSON.parse(localStorage.getItem('agentInfo') || '{}');
  const agentEmail = agentInfo.email || '';

  useEffect(() => {
    if (isOpen) {
      setToEmails(agentEmail ? [agentEmail] : []);
      setCustomEmail('');
      setAttachPdf(false);
      setAttachCsv(false);
      loadDefaults();
    }
  }, [isOpen]);

  const loadDefaults = async () => {
    try {
      const res = await getDefaultRecipients(token);
      setDefaultRecipients(res.recipients || []);
    } catch (_) {}
  };

  const toggleEmail = (email) => {
    setToEmails(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const addCustomEmail = () => {
    const e = customEmail.trim().toLowerCase();
    if (!e || !e.includes('@')) { toast.error('Enter a valid email'); return; }
    if (toEmails.includes(e)) { toast.error('Already added'); return; }
    setToEmails(prev => [...prev, e]);
    setCustomEmail('');
  };

  const removeEmail = (email) => {
    setToEmails(prev => prev.filter(e => e !== email));
  };

  const handleSend = async () => {
    if (toEmails.length === 0) { toast.error('Add at least one recipient'); return; }
    setIsSending(true);
    try {
      const result = await sendReportEmail({
        reportType,
        weekStartDate,
        toEmails,
        attachPdf,
        attachCsv,
      }, token);
      toast.success(result.message || 'Report sent!');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send report');
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  const typeLabel = reportType === 'activity' ? 'Weekly Activity' : 'Weekly Goals';

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="send-report-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <Mail size={20} className="text-brand-blue" />
            <h3 className="text-lg font-bold text-gray-900">Send {typeLabel} Report</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="close-send-modal">
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Week info */}
          <p className="text-sm text-gray-500">Week starting: <strong>{weekStartDate}</strong></p>

          {/* Your email */}
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Send to yourself</label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={toEmails.includes(agentEmail)}
                onChange={() => toggleEmail(agentEmail)}
                className="w-4 h-4"
                data-testid="send-to-self-checkbox"
              />
              <span className="text-sm text-gray-700">{agentEmail}</span>
            </label>
          </div>

          {/* Default recipients */}
          {defaultRecipients.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">Default Recipients</label>
              {defaultRecipients.map((r, i) => (
                <label key={i} className="flex items-center gap-2 cursor-pointer mb-1">
                  <input
                    type="checkbox"
                    checked={toEmails.includes(r.email)}
                    onChange={() => toggleEmail(r.email)}
                    className="w-4 h-4"
                    data-testid={`default-recipient-${i}`}
                  />
                  <span className="text-sm text-gray-700">{r.name} <span className="text-gray-400">({r.email})</span></span>
                </label>
              ))}
            </div>
          )}

          {/* Custom email */}
          <div>
            <label className="text-xs font-semibold text-gray-700 mb-1 block">Add another email</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomEmail(); }}}
                placeholder="name@example.com"
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none"
                data-testid="custom-email-input"
              />
              <button
                onClick={addCustomEmail}
                className="px-3 py-2 bg-brand-blue text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
                data-testid="add-email-btn"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          {/* Selected emails */}
          {toEmails.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-gray-700 mb-1 block">Sending to ({toEmails.length})</label>
              <div className="flex flex-wrap gap-2">
                {toEmails.map((e, i) => (
                  <span key={i} className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-brand-blue rounded-full text-xs font-medium">
                    {e}
                    <button onClick={() => removeEmail(e)} className="hover:text-red-500" data-testid={`remove-email-${i}`}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Attachment options */}
          <div className="bg-gray-50 rounded-lg p-3">
            <label className="text-xs font-semibold text-gray-700 mb-2 block flex items-center gap-1">
              <Paperclip size={12} /> Attachments (optional)
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={attachPdf} onChange={() => setAttachPdf(!attachPdf)} className="w-4 h-4" data-testid="attach-pdf-checkbox" />
                <span className="text-sm text-gray-700">Attach PDF</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={attachCsv} onChange={() => setAttachCsv(!attachCsv)} className="w-4 h-4" data-testid="attach-csv-checkbox" />
                <span className="text-sm text-gray-700">Attach CSV</span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-300 transition" data-testid="cancel-send-btn">
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={isSending || toEmails.length === 0}
            className="flex items-center gap-2 px-5 py-2 bg-brand-blue text-white rounded-lg text-sm font-semibold hover:bg-brand-orange transition disabled:opacity-50 min-h-[44px]"
            data-testid="send-report-btn"
          >
            {isSending ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Send size={16} /> Send Report</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendReportModal;
